import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, writeAuthProfileCache } from "../../context/AuthContext";
import { handleGoogleCallback, clearGoogleAuthSession, getAuthMode } from "./googleAuth";
import { typography, textColors } from "../../../design-system/tokens";
import AuthLayout from "../AuthLayout";
import { apiFetch } from "../../utils/api";
import { APP_DOMAIN } from "../../utils/config";

// In development, React.StrictMode mounts effects twice. Without a single-flight
// guard, this can issue duplicate OAuth callback POSTs where one succeeds and the
// second fails with invalid_grant, causing an erroneous redirect to login.
let inFlightCallbackKey = null;
let inFlightCallbackPromise = null;

const getCallbackKey = (code, state) => `${String(code)}::${String(state)}`;

const runGoogleCallbackFlow = async ({ code, state }) => {
  const result = await handleGoogleCallback(code, state);
  if (!result.success) {
    throw new Error("Authentication failed");
  }

  // Poll server session FIRST to ensure session is ready before setting auth.
  // suppressUnauthorizedEvent: polling 401s during OAuth are expected while the
  // session propagates and must NOT trigger the global logout event.
  const maxAttempts = 10;
  let attempt = 0;
  let serverProfile = null;
  // eslint-disable-next-line no-console
  console.log("[GoogleCallback] Starting session polling...");

  while (attempt < maxAttempts) {
    try {
      const res = await apiFetch("/api/auth/profile/", {
        suppressUnauthorizedEvent: true,
      });
      if (res.ok) {
        serverProfile = await res.json();
        // eslint-disable-next-line no-console
        console.log("[GoogleCallback] Session ready, profile:", serverProfile);
        break;
      }
      // eslint-disable-next-line no-console
      console.log(
        `[GoogleCallback] Session not ready yet, attempt ${attempt + 1}/${maxAttempts}`
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`[GoogleCallback] Session poll error attempt ${attempt + 1}:`, err);
    }
    await new Promise((r) => setTimeout(r, 300));
    attempt += 1;
  }

  if (!serverProfile) {
    throw new Error("Session not established after multiple attempts");
  }

  return { result, serverProfile };
};

const getOrStartGoogleCallbackFlow = ({ code, state }) => {
  const key = getCallbackKey(code, state);
  if (inFlightCallbackPromise && inFlightCallbackKey === key) {
    return inFlightCallbackPromise;
  }

  inFlightCallbackKey = key;
  inFlightCallbackPromise = runGoogleCallbackFlow({ code, state }).finally(() => {
    inFlightCallbackKey = null;
    inFlightCallbackPromise = null;
  });
  return inFlightCallbackPromise;
};

const GoogleAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useContext(AuthContext);

  useEffect(() => {
    let isActive = true;
    let successTimeoutId = null;
    let errorTimeoutId = null;

    const processGoogleCallback = async () => {
      try {
        // Extract parameters from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const oauthError = searchParams.get('error');

        // Handle OAuth errors from Google
        if (oauthError) {
          throw new Error(`Google OAuth error: ${oauthError}`);
        }

        // Validate required parameters
        if (!code || !state) {
          throw new Error('Missing required OAuth parameters');
        }

        // Single-flight: dedupe duplicate callback processing for the same code/state.
        const { result, serverProfile } = await getOrStartGoogleCallbackFlow({ code, state });
        if (!isActive) return;

        const mustCompleteOnboarding = !!serverProfile.must_complete_onboarding;

        // Now set auth with confirmed server data
        setAuth({
          userId: serverProfile.id,
          username: serverProfile.username,
          email: serverProfile.email,
          firstName: serverProfile.first_name,
          lastName: serverProfile.last_name,
          picture: result.user?.picture,
          mustCompleteOnboarding,
        });

        // Persist the profile to localStorage cache so that any subsequent
        // AuthProvider hydration (e.g. on navigation or window focus) picks it up
        // immediately instead of starting from scratch.
        writeAuthProfileCache(serverProfile);

        // Portal opt-in: when the Google flow was initiated from the affiliate
        // or partner login page it stores this marker. Verify portal access and
        // route back into the portal on the same prefix instead of the regular
        // app. The legacy "influencer" value is transitional: a user who
        // started the OAuth flow before the affiliate rebrand deployed may
        // still carry it.
        const returnTo = sessionStorage.getItem('googleAuthReturnTo');
        if (returnTo === 'affiliate' || returnTo === 'influencer' || returnTo === 'partner') {
          sessionStorage.removeItem('googleAuthReturnTo');
          const portalBase = returnTo === 'partner' ? '/partner' : '/affiliate';
          let isAffiliate = false;
          let affiliateMustComplete = mustCompleteOnboarding;
          try {
            // Backend route unchanged: the API still exposes /api/influencer/check/.
            const checkRes = await apiFetch('/api/influencer/check/', {
              suppressUnauthorizedEvent: true,
            });
            const checkData = await checkRes.json().catch(() => null);
            isAffiliate = checkRes.ok;
            if (checkRes.ok) {
              affiliateMustComplete = !!(
                checkData?.user?.must_complete_onboarding ?? mustCompleteOnboarding
              );
            } else if (
              checkRes.status === 403 &&
              checkData?.code === 'affiliate_portal_blocked'
            ) {
              navigate(`${portalBase}/login`, {
                state: {
                  code: 'affiliate_portal_blocked',
                  error:
                    checkData?.error ||
                    "Access to the Affiliate Portal is currently unavailable for this account.",
                },
              });
              return;
            }
          } catch (_e) {
            isAffiliate = false;
          }
          if (!isActive) return;

          if (isAffiliate) {
            if (affiliateMustComplete) {
              navigate(
                `/edit/complete-account?returnTo=${encodeURIComponent(`${portalBase}/dashboard/coupons`)}`
              );
            } else {
              navigate(`${portalBase}/dashboard/coupons`);
            }
          } else {
            navigate(`${portalBase}/login`, {
              state: {
                error:
                  returnTo === 'partner'
                    ? 'This account does not have portal access.'
                    : 'This account is not an affiliate.',
              },
            });
          }
          return;
        }

        console.log("[GoogleCallback] Auth context set, navigating to dashboard");
        setIsNewUser(!!result.isNewUser);
        setStatus('success');

        // Small delay for success message, then redirect
        successTimeoutId = window.setTimeout(() => {
          if (!isActive) return;
          if (mustCompleteOnboarding) {
            navigate('/edit/complete-account');
            return;
          }
          navigate(`/edit/${serverProfile.username}`);
        }, 1000);

      } catch (err) {
        if (!isActive) return;
        console.error('Google OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
        setStatus('error');

        const authMode = getAuthMode();
        // Clear any stored auth session data (including the affiliate return marker).
        clearGoogleAuthSession();
        sessionStorage.removeItem('googleAuthReturnTo');

        // Redirect to login page after showing error
        errorTimeoutId = window.setTimeout(() => {
          if (!isActive) return;
          const redirectPath = authMode === 'register' ? '/edit/register' : '/edit/login';
          navigate(redirectPath);
        }, 3000);
      }
    };

    processGoogleCallback();
    return () => {
      isActive = false;
      if (successTimeoutId) window.clearTimeout(successTimeoutId);
      if (errorTimeoutId) window.clearTimeout(errorTimeoutId);
    };
  }, [searchParams, setAuth, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <h2 className={`${typography.h3} ${textColors.primary} mb-2`}>Completing sign-in...</h2>
            <p className={`${typography.bodyLarge} ${textColors.secondary}`}>Please wait while we finish setting up your account.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className={`${typography.h3} ${textColors.primary} mb-2`}>
              {isNewUser ? `Welcome to ${APP_DOMAIN}!` : 'Welcome back!'}
            </h2>
            <p className={`${typography.bodyLarge} ${textColors.secondary}`}>
              {isNewUser 
                ? 'Your account has been created successfully. Redirecting to your dashboard...'
                : 'You have been signed in successfully. Redirecting to your dashboard...'
              }
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className={`${typography.h3} ${textColors.primary} mb-2`}>Authentication Failed</h2>
            <p className={`${typography.bodyLarge} ${textColors.secondary} mb-4`}>{error}</p>
            <p className={`${typography.bodySmall} ${textColors.tertiary}`}>Redirecting you back to sign in...</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AuthLayout>{renderContent()}</AuthLayout>
  );
};

export default GoogleAuthCallback;
